import React, { useEffect, useRef } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { THEME } from "../../common";
import type { ValueOf } from "../../common/utility-types";
import { useExcalidrawAppState } from "../App";
import MenuItemContent from "./DropdownMenuItemContent";
import { getDropdownMenuItemClassName } from "./common";
import { DropdownMenuContentPropsContext } from "./common";
import { useContext } from "react";
import type { JSX } from "react";

const DropdownMenuItem = ({
  icon,
  value,
  order,
  children,
  shortcut,
  className,
  hovered,
  selected,
  textStyle,
  onSelect,
  onClick,
  badge,
  ...rest
}: {
  icon?: JSX.Element;
  value?: string | number | undefined;
  order?: number;
  onSelect?: (event: Event) => void;
  children: React.ReactNode;
  shortcut?: string;
  hovered?: boolean;
  selected?: boolean;
  textStyle?: React.CSSProperties;
  className?: string;
  badge?: React.ReactNode;
  onClick,
} & Omit<
  React.ComponentProps<typeof DropdownMenuPrimitive.Item>,
  "onSelect" | "onClick"
>) => {
  const contentProps = useContext(DropdownMenuContentPropsContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hovered && ref.current) {
      if (order === 0) {
        ref.current.scrollIntoView({ block: "end" });
      } else {
        ref.current.scrollIntoView({ block: "nearest" });
      }
    }
  }, [hovered, order]);

  const handleSelect = (e: Event) => {
    onClick?.(e as unknown as React.MouseEvent);
    onSelect?.(e);
    contentProps.onSelect?.(e);
  };

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      onSelect={(e) => handleSelect(e as unknown as Event)}
      textValue={typeof children === "string" ? children : undefined}
      className={getDropdownMenuItemClassName(className, selected, hovered)}
      title={(rest as any).title ?? (rest as any)["aria-label"]}
      {...rest}
    >
      <MenuItemContent
        textStyle={textStyle}
        icon={icon}
        shortcut={shortcut}
        badge={badge}
      >
        {children}
      </MenuItemContent>
    </DropdownMenuPrimitive.Item>
  );
};
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropDownMenuItemBadgeType = {
  GREEN: "green",
  RED: "red",
  BLUE: "blue",
} as const;

export const DropDownMenuItemBadge = ({
  type = DropDownMenuItemBadgeType.BLUE,
  children,
}: {
  type?: ValueOf<typeof DropDownMenuItemBadgeType>;
  children: React.ReactNode;
}) => {
  const { theme } = useExcalidrawAppState();
  const style: React.CSSProperties = {
    display: "inline-flex",
    marginLeft: "auto",
    padding: "2px 4px",
    borderRadius: 6,
    fontSize: 9,
    fontFamily: "Cascadia, monospace",
    border: theme === THEME.LIGHT ? "1.5px solid white" : "none",
  };

  switch (type) {
    case DropDownMenuItemBadgeType.GREEN:
      Object.assign(style, {
        backgroundColor: "var(--background-color-badge)",
        color: "var(--color-badge)",
      });
      break;
    case DropDownMenuItemBadgeType.RED:
      Object.assign(style, {
        backgroundColor: "pink",
        color: "darkred",
      });
      break;
    case DropDownMenuItemBadgeType.BLUE:
    default:
      Object.assign(style, {
        background: "var(--color-promo)",
        color: "var(--color-surface-lowest)",
      });
  }

  return (
    <div className="DropDownMenuItemBadge" style={style}>
      {children}
    </div>
  );
};
DropDownMenuItemBadge.displayName = "DropdownMenuItemBadge";

DropdownMenuItem.Badge = DropDownMenuItemBadge;

export default DropdownMenuItem;
